import { useMembershipRoleWatch } from './useMembershipRoleWatch';
import { useSessionRevocationWatch } from './useSessionRevocationWatch';
import { useResumeLivenessProbe } from './useResumeLivenessProbe';
import { useDeactivationPoll } from './useDeactivationPoll';

/**
 * The four AuthContext session watches, extracted verbatim (500-line cap).
 * Called from the same position in AuthProvider, so hook order is unchanged.
 */
export function useAuthSessionWatches({
  sessionUserId, tenantId, loading, tenantLoading, role, offlineSinceRef, syncSessionRef, signOut, setAuthNotice,
}) {
  // Periodic deactivation check — extracted to useDeactivationPoll.js (P1
  // access-status addendum, 2026-09-24). The one behavior change from the
  // previous inline version: the first check now also runs IMMEDIATELY on a
  // session's first tenant resolution, not only after the first
  // DEACTIVATION_POLL_MS interval — closing the window where a stale-JWT
  // deactivation (member or company, deactivated after this token was
  // minted) could render the app shell with every query denied by RLS for
  // up to 5 minutes. See that file for the full rationale.
  useDeactivationPoll({
    sessionUserId, tenantId, loading, tenantLoading, role, offlineSinceRef, syncSessionRef,
  });

  // Role-agnostic resume liveness probe (2026-07-27 P0-2 fix) — extracted,
  // see useResumeLivenessProbe.js for the full rationale.
  useResumeLivenessProbe(sessionUserId);

  // Role-change realtime reaction (extracted — see useMembershipRoleWatch): own
  // tenant_memberships row UPDATE with role drift / deactivation → context signOut
  // (cache wipes) + roleChanged notice. Edge-fn session revoke is the backstop.
  useMembershipRoleWatch({
    sessionUserId, tenantId, loading, tenantLoading, role, signOut, setAuthNotice,
  });

  // Account Sessions / offboarding revoke → Realtime kick (see eng plan
  // session hard-reject). REST db_pre_request is the hostile-client backstop
  // once enabled; this is the honest-client instant path.
  useSessionRevocationWatch({
    sessionUserId, loading, signOut, setAuthNotice,
  });
}
